import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { StaffShell } from '../../components/StaffShell';
import { ClinicianBiomarkerUpload } from '../../components/ClinicianBiomarkerUpload';
import { EditPatientModal } from '../../components/EditPatientModal';
import { LabUploadHistory } from '../../components/LabUploadHistory';
import { PatientAvatar } from '../../components/PatientAvatar';
import { useUserStore } from '../../store/userStore';
import { getPatientSummary, getClinicalOptions, saveClinicalRecord, updateTriageStatus, getMessages, sendMessage, createReminder, getLabUploads } from '../../lib/api';

const TRIAGE_OPTIONS = [
  { value: 'not_reviewed', label: 'Not reviewed' },
  { value: 'in_review', label: 'In review' },
  { value: 'awaiting_biomarkers', label: 'Awaiting biomarkers' },
  { value: 'reviewed_plan_issued', label: 'Reviewed / plan issued' },
  { value: 'follow_up_due', label: 'Follow-up due' },
  { value: 'closed', label: 'Closed' },
];

function Section({ title, children, icon }) {
  return (
    <div className="glass-dark rounded-2xl p-5 mb-4">
      <h3 className="font-display text-lg text-white mb-3 flex items-center gap-2">{icon && <i className={`fa-solid ${icon} text-[var(--aura-purple-light)]`} />}{title}</h3>
      {children}
    </div>
  );
}

export default function PatientSummary() {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const clinician = useUserStore((s) => s.user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ primary_driver: '', secondary_contributor: '', treatment_plan_components: [], follow_up_interval: '', clinical_notes: '' });
  const [triage, setTriage] = useState('not_reviewed');

  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [reminderText, setReminderText] = useState('');
  const [reminderTitle, setReminderTitle] = useState('Follow-up reminder');
  const [sendingRem, setSendingRem] = useState(false);

  const loadMessages = () => getMessages(patientId).then((m) => setMessages((m || []).slice().reverse())).catch(() => {});
  const reloadSummary = () => getPatientSummary(clinician.id, patientId).then(setData).catch(() => {});
  const [editing, setEditing] = useState(false);
  const [uploads, setUploads] = useState([]);
  const loadUploads = () => getLabUploads(clinician.id, patientId).then(setUploads).catch(() => {});
  useEffect(() => { if (clinician?.id && patientId) loadUploads(); }, [clinician?.id, patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const clinThreadRef = useRef(null);
  useEffect(() => {
    if (clinThreadRef.current) clinThreadRef.current.scrollTop = clinThreadRef.current.scrollHeight;
  }, [messages]);

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    setSendingMsg(true);
    try {
      await sendMessage({ user_id: patientId, sender_type: 'clinician', content: msgText.trim() });
      setMsgText('');
      await loadMessages();
      toast.success('Message sent to patient');
    } catch (e) {
      toast.error('Could not send message');
    } finally {
      setSendingMsg(false);
    }
  };

  const sendReminder = async () => {
    if (!reminderText.trim()) return;
    setSendingRem(true);
    try {
      await createReminder(patientId, reminderTitle.trim() || 'Follow-up reminder', reminderText.trim());
      setReminderText('');
      toast.success('Follow-up reminder scheduled');
    } catch (e) {
      toast.error('Could not send reminder');
    } finally {
      setSendingRem(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [summary, options] = await Promise.all([
          getPatientSummary(clinician.id, patientId),
          getClinicalOptions(),
        ]);
        setData(summary);
        setOpts(options);
        const cr = summary.clinical_record;
        if (cr) {
          setForm({
            primary_driver: cr.primary_driver || '',
            secondary_contributor: cr.secondary_contributor || '',
            treatment_plan_components: cr.treatment_plan_components || [],
            follow_up_interval: cr.follow_up_interval || '',
            clinical_notes: cr.clinical_notes || '',
          });
          setTriage(cr.triage_status || 'not_reviewed');
        }
        loadMessages();
      } finally {
        setLoading(false);
      }
    })();
  }, [patientId, clinician.id]);

  const toggleComponent = (c) =>
    setForm((p) => ({
      ...p,
      treatment_plan_components: p.treatment_plan_components.includes(c)
        ? p.treatment_plan_components.filter((x) => x !== c)
        : [...p.treatment_plan_components, c],
    }));

  const saveRecord = async () => {
    setSaving(true);
    try {
      await saveClinicalRecord(clinician.id, patientId, { patient_id: patientId, clinician_id: clinician.id, ...form });
      toast.success('Clinical record saved');
    } catch (e) {
      toast.error('Could not save record');
    } finally {
      setSaving(false);
    }
  };

  const changeTriage = async (value) => {
    setTriage(value);
    try {
      await updateTriageStatus(clinician.id, patientId, value);
      toast.success('Triage status updated');
    } catch (e) {
      toast.error('Could not update status');
    }
  };

  if (loading) return <StaffShell title="Clinician"><div className="text-white/50 text-center py-16"><i className="fa-solid fa-circle-notch fa-spin text-2xl" /></div></StaffShell>;
  if (!data) return <StaffShell title="Clinician"><div className="text-white/50 text-center py-16">Patient not found.</div></StaffShell>;

  const p = data.patient;
  const domains = data.assessment?.domains || {};

  return (
    <StaffShell title="Clinician" badge="Patient review">
      {editing && (
        <EditPatientModal
          clinicianId={clinician.id}
          patient={p}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); reloadSummary(); }}
        />
      )}
      <button onClick={() => navigate('/clinician')} className="text-white/60 hover:text-white text-sm flex items-center gap-2 mb-4"><i className="fa-solid fa-arrow-left" /> Back to triage</button>

      <div className="glass-dark rounded-2xl p-5 sm:p-6 mb-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <PatientAvatar patient={p} size="md" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-display text-xl sm:text-2xl text-white truncate" data-testid="patient-header-name">{p.name}</div>
              <button
                data-testid="edit-patient-btn"
                onClick={() => setEditing(true)}
                title="Edit patient details"
                className="grid place-items-center w-8 h-8 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-base shrink-0"
              >
                <i className="fa-solid fa-pen text-xs" />
              </button>
            </div>
            <div className="text-white/50 text-sm truncate">{p.email?.endsWith('@patients.auralis.local') ? 'No login email on record' : p.email}</div>
            <div className="text-white/50 text-sm" data-testid="patient-header-meta">Age {p.age} · <span className="capitalize">{p.stage}</span>{p.date_of_birth ? <> · DOB {p.date_of_birth}</> : null}</div>
            <button
              data-testid="open-patient-record-btn"
              onClick={() => navigate(`/clinician/patient/${patientId}/record`)}
              className="mt-2 inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1.5 bg-[rgba(168,85,247,0.2)] border border-[rgba(168,85,247,0.5)] text-white hover:bg-[rgba(168,85,247,0.35)] transition-base"
            >
              <i className="fa-solid fa-folder-open" /> Open patient record · results visibility
            </button>
          </div>
        </div>
        <div className="w-full sm:w-auto shrink-0">
          <label className="block text-xs text-white/40 mb-1">Triage status</label>
          <select data-testid="triage-select" value={triage} onChange={(e) => changeTriage(e.target.value)} className="w-full sm:w-auto rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none">
            {TRIAGE_OPTIONS.map((o) => <option key={o.value} value={o.value} className="text-black">{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <Section title="Dominant pattern" icon="fa-diagram-project">
            <div className="text-white text-lg font-medium">{data.dominant_pattern}</div>
            {data.primary_domains?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {data.primary_domains.map((d, i) => <span key={i} className="text-xs rounded-full px-3 py-1 bg-white/10 text-white/80">{d.name} · {d.status}</span>)}
              </div>
            )}
          </Section>

          {Object.keys(domains).length > 0 && (
            <Section title="Assessment domains" icon="fa-wave-square">
              <div className="space-y-3">
                {Object.entries(domains).map(([k, d]) => (
                  <div key={k}>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/80 capitalize">{k.replace(/_/g, ' ')}</span>
                      <span className="text-white/60 capitalize">{d.status}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/10 mt-1 overflow-hidden">
                      <div className="h-full rounded-full bg-[var(--aura-purple-light)]" style={{ width: `${Math.min(100, d.score || 0)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title={`Domain check-ins (${data.domain_checkin_count || 0})`} icon="fa-clipboard-list">
            {Object.keys(data.domain_checkins || {}).length === 0 ? (
              <p className="text-white/40 text-sm">No domain check-ins recorded.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.domain_checkins).map(([dom, list]) => (
                  <span key={dom} className="text-xs rounded-full px-3 py-1 bg-white/10 text-white/80 capitalize">{dom}: {list.length}</span>
                ))}
              </div>
            )}
          </Section>

          <Section title="In the patient's own words" icon="fa-comment-heart">
            {p.wellbeing_statement ? (
              <div data-testid="patient-wellbeing-statement">
                <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">“{p.wellbeing_statement}”</p>
                {p.wellbeing_statement_updated_at ? <div className="text-[11px] text-white/40 mt-2">Updated {new Date(p.wellbeing_statement_updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div> : null}
              </div>
            ) : (
              <p className="text-white/40 text-sm" data-testid="patient-wellbeing-empty">The patient hasn't written anything yet.</p>
            )}
          </Section>

          <Section title="Recent daily trends" icon="fa-chart-line">
            {data.daily_trends?.length ? (
              <div className="space-y-1.5">
                {data.daily_trends.map((t, i) => (
                  <div key={i} className="flex justify-between text-sm text-white/70">
                    <span>{t.date}</span>
                    <span>Mood {t.mood ?? '–'} · Energy {t.energy ?? '–'} · Sleep {t.sleep ?? '–'} · Stress {t.stress ?? '–'}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-white/40 text-sm">No recent check-ins.</p>}
          </Section>

          <Section title="Biomarkers" icon="fa-vial">
            {data.biomarkers?.length ? (
              <div className="space-y-2 mb-4">
                {data.biomarkers.map((b, i) => (
                  <div key={i} className="flex justify-between text-sm text-white/70">
                    <span>{b.test_type} · {b.test_date}</span>
                    <span>{b.flags?.length ? `${b.flags.length} flagged` : 'Normal'}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-white/40 text-sm mb-4">No biomarker results.</p>}
            <ClinicianBiomarkerUpload patientId={patientId} onSaved={() => { reloadSummary(); loadUploads(); }} onUploaded={loadUploads} />
            <div className="mt-5 pt-4 border-t border-white/10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-white/40 mb-1"><i className="fa-solid fa-clock-rotate-left" /> Upload history</div>
              <LabUploadHistory uploads={uploads} />
            </div>
          </Section>
        </div>

        {/* Clinical review form */}
        <div>
          <Section title="Clinical review" icon="fa-stethoscope">
            <label className="block text-xs text-white/50 mb-1">Primary driver</label>
            <select data-testid="primary-driver" value={form.primary_driver} onChange={(e) => setForm({ ...form, primary_driver: e.target.value })} className="w-full mb-3 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none">
              <option value="" className="text-black">Select…</option>
              {opts?.primaryDrivers.map((o) => <option key={o} value={o} className="text-black">{o}</option>)}
            </select>

            <label className="block text-xs text-white/50 mb-1">Secondary contributor</label>
            <select value={form.secondary_contributor} onChange={(e) => setForm({ ...form, secondary_contributor: e.target.value })} className="w-full mb-3 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none">
              <option value="" className="text-black">Select…</option>
              {opts?.secondaryContributors.map((o) => <option key={o} value={o} className="text-black">{o}</option>)}
            </select>

            <label className="block text-xs text-white/50 mb-2">Treatment plan components</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {opts?.treatmentComponents.map((c) => {
                const sel = form.treatment_plan_components.includes(c);
                return (
                  <button key={c} onClick={() => toggleComponent(c)} className="text-xs rounded-full px-3 py-1.5 border transition-base" style={sel ? { background: 'var(--aura-purple)', borderColor: 'var(--aura-purple)', color: '#fff' } : { borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)' }}>
                    {c}
                  </button>
                );
              })}
            </div>

            <label className="block text-xs text-white/50 mb-1">Follow-up interval</label>
            <select value={form.follow_up_interval} onChange={(e) => setForm({ ...form, follow_up_interval: e.target.value })} className="w-full mb-3 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none">
              <option value="" className="text-black">Select…</option>
              {opts?.followUpIntervals.map((o) => <option key={o} value={o} className="text-black">{o}</option>)}
            </select>

            <label className="block text-xs text-white/50 mb-1">Clinical notes</label>
            <textarea data-testid="clinical-notes" rows={4} value={form.clinical_notes} onChange={(e) => setForm({ ...form, clinical_notes: e.target.value })} className="w-full rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none resize-none mb-4" placeholder="Assessment, plan, prescriptions, signposting…" />

            <button data-testid="save-record-btn" onClick={saveRecord} disabled={saving} className="w-full aura-btn-gradient text-white font-semibold rounded-2xl py-3 flex items-center justify-center gap-2 disabled:opacity-70">
              {saving ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-floppy-disk" /> Save clinical record</>}
            </button>
          </Section>

          <Section title="Message patient" icon="fa-comment-medical">
            <div ref={clinThreadRef} className="max-h-52 overflow-y-auto space-y-2 mb-3 pr-1">
              {messages.length === 0 ? (
                <p className="text-white/40 text-sm">No messages yet. Start the conversation below.</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === 'clinician' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className="max-w-[80%] rounded-2xl px-3 py-2 text-sm"
                      style={m.sender_type === 'clinician'
                        ? { background: 'var(--aura-purple)', color: '#fff' }
                        : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.9)' }}
                    >
                      {m.content}
                      <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="clinician-message-input"
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMsg()}
                placeholder="Write a message to the patient…"
                className="flex-1 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none placeholder-white/40"
              />
              <button data-testid="send-message-btn" onClick={sendMsg} disabled={sendingMsg} className="aura-btn-gradient text-white rounded-xl px-4 disabled:opacity-70">
                {sendingMsg ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-paper-plane" />}
              </button>
            </div>
          </Section>

          <Section title="Follow-up reminder" icon="fa-bell">
            <input
              data-testid="reminder-title-input"
              value={reminderTitle}
              onChange={(e) => setReminderTitle(e.target.value)}
              placeholder="Reminder title"
              className="w-full mb-2 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none placeholder-white/40"
            />
            <textarea
              data-testid="reminder-body-input"
              rows={3}
              value={reminderText}
              onChange={(e) => setReminderText(e.target.value)}
              placeholder="e.g. Please book your 6-week review and repeat bloods before we meet."
              className="w-full mb-3 rounded-xl bg-white/10 text-white border border-white/10 px-3 py-2.5 outline-none resize-none placeholder-white/40"
            />
            <button data-testid="send-reminder-btn" onClick={sendReminder} disabled={sendingRem} className="w-full rounded-2xl py-3 flex items-center justify-center gap-2 font-semibold text-white disabled:opacity-70" style={{ background: '#EC4899' }}>
              {sendingRem ? <i className="fa-solid fa-circle-notch fa-spin" /> : <><i className="fa-solid fa-bell" /> Send reminder</>}
            </button>
          </Section>
        </div>
      </div>
    </StaffShell>
  );
}
